export const movementReasonOptions = [
  { value: "EXPENSE", label: "Gasto" },
  { value: "WITHDRAWAL", label: "Retiro" },
  { value: "OTHER", label: "Otro" },
];

export const movementChannelOptions = [
  { value: "CASH", label: "Efectivo" },
  { value: "CARD", label: "Tarjeta" },
  { value: "TRANSFER", label: "Transferencia" },
];

const reasonLabels = {
  SALE: "Venta",
  DEPOSIT: "Deposito",
  WITHDRAWAL: "Retiro",
  EXPENSE: "Gasto",
  OTHER: "Otro",
};

const channelLabels = {
  CASH: "Efectivo",
  CARD: "Tarjeta",
  TRANSFER: "Transferencia",
};

const movementStatusLabels = {
  POSTED: "Publicado",
  VOID: "Anulado",
};

const sessionStatusLabels = {
  OPEN: "Abierta",
  CLOSED: "Cerrada",
  RECONCILED: "Conciliada",
};

const cashOfficeStatusLabels = {
  OPEN: "Abierta",
  PENDING_RECEIPT: "Pendiente recibir",
  RECEIVED: "Recibida con diferencia",
  BALANCED: "Cuadrada",
  NONE: "-",
};

export const EMPTY_SUMMARY = Object.freeze({
  opening_float: "0.00",
  expected_cash: "0.00",
  expected_card: "0.00",
  expected_transfer: "0.00",
  expenses: "0.00",
  total_expected: "0.00",
  counted_cash: null,
  counted_card: null,
  counted_transfer: null,
  difference_cash: null,
  difference_card: null,
  difference_transfer: null,
});

export const buildEmptySummary = () => ({ ...EMPTY_SUMMARY });

export const buildEmptyCurrentSession = () => ({
  session: null,
  cash_register: null,
  summary: buildEmptySummary(),
  movements: [],
  sales: [],
});

export const buildEmptySessionDetail = () => ({
  session: null,
  summary: buildEmptySummary(),
  movements: [],
  sales: [],
  cash_office_status: "NONE",
});

export const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.results)) return value.results;
  return [];
};

export const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const toMoney = (value) =>
  `Q ${toNumber(value).toLocaleString("es-GT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const toInputMoney = (value) => toNumber(value).toFixed(2);

export const toSignedMoney = (value) => {
  const parsed = toNumber(value);
  const sign = parsed > 0 ? "+" : "";
  return `${sign}Q ${parsed.toLocaleString("es-GT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = date.getFullYear();
  const hh = String(date.getHours()).padStart(2, "0");
  const mi = String(date.getMinutes()).padStart(2, "0");
  return `${dd}/${mm}/${yyyy} ${hh}:${mi}`;
};

export const formatTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  const hh = String(date.getHours()).padStart(2, "0");
  const mi = String(date.getMinutes()).padStart(2, "0");
  return `${hh}:${mi}`;
};

export const getMovementReasonLabel = (value) => reasonLabels[value] || value || "-";

export const getMovementChannelLabel = (value) => channelLabels[value] || value || "-";

export const getMovementStatusLabel = (value) => movementStatusLabels[value] || value || "-";

export const getSessionStatusLabel = (value) => sessionStatusLabels[value] || value || "-";

export const getSessionStatusColor = (value) => {
  if (value === "OPEN") return "warning";
  if (value === "RECONCILED") return "success";
  return "default";
};

export const getCashOfficeStatusLabel = (value) => cashOfficeStatusLabels[value] || value || "-";

export const getCashOfficeStatusColor = (value) => {
  if (value === "BALANCED") return "success";
  if (value === "RECEIVED") return "warning";
  if (value === "PENDING_RECEIPT") return "default";
  if (value === "OPEN") return "warning";
  return "default";
};

export const normalizeSummary = (summary) => ({
  ...buildEmptySummary(),
  ...(summary || {}),
});

export const normalizeCurrentSession = (data) => ({
  ...buildEmptyCurrentSession(),
  ...(data || {}),
  summary: normalizeSummary(data?.summary),
  movements: toArray(data?.movements),
  sales: toArray(data?.sales),
});

export const normalizeSessionDetail = (data) => ({
  ...buildEmptySessionDetail(),
  ...(data || {}),
  summary: normalizeSummary(data?.summary),
  movements: toArray(data?.movements),
  sales: toArray(data?.sales),
});

export const getMovementSignedAmount = (movement) => {
  const amount = toNumber(movement?.amount);
  return movement?.movement_type === "OUT" ? amount * -1 : amount;
};

export const getMovementAmountColor = (movement) =>
  getMovementSignedAmount(movement) < 0 ? "error.main" : "success.main";

export const getDifferenceColor = (value, isSessionOpen = false) => {
  if (isSessionOpen) return "text.secondary";
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed === 0) return "text.primary";
  return parsed < 0 ? "error.main" : "success.main";
};

export const calculateMovementTotals = (movements) =>
  toArray(movements).reduce(
    (acc, movement) => {
      if (movement?.status !== "POSTED") return acc;
      const amount = toNumber(movement.amount);
      if (movement?.movement_type === "OUT") {
        acc.outflow += amount;
        acc.net -= amount;
        return acc;
      }
      acc.inflow += amount;
      acc.net += amount;
      return acc;
    },
    { inflow: 0, outflow: 0, net: 0 }
  );

export const calculateAdditionalMovementTotals = (movements) =>
  toArray(movements).reduce(
    (acc, movement) => {
      if (movement?.status !== "POSTED" || movement?.reason_type === "SALE") return acc;
      const amount = toNumber(movement.amount);
      if (movement?.movement_type === "OUT") {
        acc.outflow += amount;
        acc.net -= amount;
        return acc;
      }
      acc.inflow += amount;
      acc.net += amount;
      return acc;
    },
    { inflow: 0, outflow: 0, net: 0 }
  );

export const calculateSalesSessionMetrics = (sales) =>
  toArray(sales).reduce(
    (acc, sale) => {
      const saleTotal = toNumber(sale?.total);
      acc.salesCount += 1;
      acc.collectedTotal += saleTotal;
      toArray(sale?.items).forEach((item) => {
        const qty = toNumber(item?.qty_presentation ?? item?.quantity ?? 0);
        acc.productsSold += qty;
      });
      if (!toArray(sale?.items).length) {
        acc.productsSold += toNumber(sale?.items_count || 0);
      }

      const payments = toArray(sale?.payments);
      if (payments.length > 0) {
        payments.forEach((payment) => {
          const label =
            payment?.payment_method_name ||
            payment?.payment_method_code ||
            sale?.primary_payment_label ||
            "Sin metodo";
          acc.paymentsByMethod[label] = (acc.paymentsByMethod[label] || 0) + toNumber(payment?.amount);
        });
      } else {
        const label = String(sale?.primary_payment_label || "Sin metodo").trim() || "Sin metodo";
        acc.paymentsByMethod[label] = (acc.paymentsByMethod[label] || 0) + saleTotal;
      }
      return acc;
    },
    { salesCount: 0, productsSold: 0, collectedTotal: 0, paymentsByMethod: {} }
  );

export const calculateClosureSummary = ({ summary, session, salesMetrics, movementTotals }) => {
  const expectedTotal =
    toNumber(summary?.total_expected) ||
    toNumber(summary?.opening_float) + toNumber(salesMetrics?.collectedTotal) + toNumber(movementTotals?.net);
  const countedTotal =
    summary?.counted_cash == null && summary?.counted_card == null && summary?.counted_transfer == null
      ? null
      : toNumber(summary?.counted_cash) + toNumber(summary?.counted_card) + toNumber(summary?.counted_transfer);
  const difference =
    countedTotal == null
      ? null
      : toNumber(summary?.difference_cash) + toNumber(summary?.difference_card) + toNumber(summary?.difference_transfer);
  const receivedCashOffice =
    session?.received_cash == null && session?.received_transfer == null && session?.received_voucher == null
      ? null
      : toNumber(session?.received_cash) + toNumber(session?.received_transfer) + toNumber(session?.received_voucher);
  const pendingReceipt =
    receivedCashOffice == null
      ? countedTotal ?? expectedTotal
      : Math.max(0, (countedTotal ?? expectedTotal) - receivedCashOffice);
  const status =
    session?.received_at || receivedCashOffice != null
      ? Math.abs(toNumber(session?.received_difference_cash)) <= 0.000001
        ? "Cuadrado"
        : "Con diferencia"
      : "Pendiente";
  return { expectedTotal, countedTotal, difference, receivedCashOffice, pendingReceipt, status };
};

export const getSessionNet = (summary) =>
  toNumber(summary?.expected_cash) +
  toNumber(summary?.expected_card) +
  toNumber(summary?.expected_transfer) -
  toNumber(summary?.opening_float);

export const uniqueRegistersFromSessions = (sessions) => {
  const mapped = new Map();
  toArray(sessions).forEach((session) => {
    const register = session?.cash_register;
    if (!register?.id || mapped.has(register.id)) return;
    mapped.set(register.id, {
      id: register.id,
      code: register.code || "",
      name: register.name || register.code || "Caja",
      label: [register.code, register.name].filter(Boolean).join(" - ") || `Caja ${register.id}`,
    });
  });
  return Array.from(mapped.values()).sort((left, right) => left.label.localeCompare(right.label));
};

export const getVoidedByLabel = (movement) =>
  movement?.voided_by_name || (movement?.voided_by_id ? `Usuario #${movement.voided_by_id}` : "Sin registro");
